import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import type { PickerValidDate } from '@mui/x-date-pickers/models';
import type { PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import moment, { type Moment } from 'moment';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { isRangeTooWide, MAX_RANGE_DAYS, maxRangeMessage } from 'utils/validateDateRange';

import { BASE_CALENDAR_SX } from './calendarSx';
import type { DateRangeValue } from './types';

// ─── Presets ───────────────────────────────────────────────────────────────────

interface DateRangePreset {
  key: string;
  label: string;
  build: () => DateRangeValue;
}

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: 'last24h',
    label: 'Últimas 24 horas',
    build: () => ({ start: moment().subtract(24, 'hours'), end: moment() }),
  },
  {
    key: 'today',
    label: 'Hoy',
    build: () => ({ start: moment().startOf('day'), end: moment().endOf('day') }),
  },
  {
    key: 'last7days',
    label: 'Últimos 7 días',
    build: () => ({ start: moment().subtract(7, 'days').startOf('day'), end: moment().endOf('day') }),
  },
  {
    key: 'thisWeek',
    label: 'Esta semana',
    build: () => ({ start: moment().startOf('isoWeek'), end: moment().endOf('isoWeek') }),
  },
  {
    key: 'lastWeek',
    label: 'Semana pasada',
    build: () => ({
      start: moment().subtract(1, 'week').startOf('isoWeek'),
      end: moment().subtract(1, 'week').endOf('isoWeek'),
    }),
  },
  {
    key: 'last30days',
    label: 'Últimos 30 días',
    build: () => ({ start: moment().subtract(30, 'days').startOf('day'), end: moment().endOf('day') }),
  },
  {
    key: 'thisMonth',
    label: 'Este mes',
    build: () => ({ start: moment().startOf('month'), end: moment().endOf('day') }),
  },
  {
    key: 'lastMonth',
    label: 'Mes pasado',
    build: () => ({
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    }),
  },
];

/** Returns the preset key whose start/end (rounded to the minute) match the given range, or null. */
export const matchPresetKey = (dr: DateRangeValue): string | null => {
  if (!dr.start || !dr.end) return null;
  for (const preset of DATE_RANGE_PRESETS) {
    const candidate = preset.build();
    if (
      candidate.start &&
      candidate.end &&
      Math.abs(candidate.start.diff(dr.start, 'minutes')) < 1 &&
      Math.abs(candidate.end.diff(dr.end, 'minutes')) < 1
    ) {
      return preset.key;
    }
  }
  return null;
};

// ─── RangeDay (custom day slot) ────────────────────────────────────────────────

interface RangeDayExtraProps {
  rangeStart: Moment | null;
  rangeEnd: Moment | null;
  hoverDate: Moment | null;
  selecting: 'start' | 'end';
  onDayHover: (day: Moment | null) => void;
}

const RangeDay: React.FC<PickerDayProps & RangeDayExtraProps> = (props) => {
  const { day, rangeStart, rangeEnd, hoverDate, selecting, onDayHover, outsideCurrentMonth, ...pickerDayProps } = props;

  const m = moment(day as unknown as Moment);

  if (outsideCurrentMonth) {
    return (
      <PickerDay {...pickerDayProps} day={day} outsideCurrentMonth selected={false} sx={{ visibility: 'hidden' }} />
    );
  }

  // Determine effective range for highlighting
  const effectiveEnd = rangeEnd ?? (selecting === 'end' ? hoverDate : null);
  const effectiveStart = rangeStart;

  const isStart = effectiveStart ? m.isSame(effectiveStart, 'day') : false;
  const isEnd = effectiveEnd ? m.isSame(effectiveEnd, 'day') : false;

  // Compute ordered bounds for in-range check
  let orderedFirst = effectiveStart;
  let orderedLast = effectiveEnd;
  if (orderedFirst && orderedLast?.isBefore(orderedFirst, 'day')) {
    orderedFirst = effectiveEnd;
    orderedLast = effectiveStart;
  }

  const isInRange =
    orderedFirst && orderedLast ? m.isAfter(orderedFirst, 'day') && m.isBefore(orderedLast, 'day') : false;
  const isSelected = isStart || isEnd;
  const hasRange = orderedFirst && orderedLast && !orderedFirst.isSame(orderedLast, 'day');

  // Determine which end this day is (for corridor shape)
  const isRangeStart = orderedFirst ? m.isSame(orderedFirst, 'day') : false;
  const isRangeEnd = orderedLast ? m.isSame(orderedLast, 'day') : false;

  return (
    <Box
      onMouseEnter={() => onDayHover(m)}
      sx={{
        position: 'relative',
        // Range corridor background strip
        '&::before':
          hasRange && (isInRange || (isRangeStart && !isRangeEnd) || (isRangeEnd && !isRangeStart))
            ? {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: isRangeStart ? '50%' : 0,
                right: isRangeEnd ? '50%' : 0,
                height: 36,
                transform: 'translateY(-50%)',
                backgroundColor: `${NAVY}0A`,
                zIndex: 0,
              }
            : {},
      }}
    >
      <PickerDay
        {...pickerDayProps}
        day={day}
        outsideCurrentMonth={false}
        selected={false}
        sx={{
          position: 'relative',
          zIndex: 1,
          borderRadius: '50%',
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.85rem',
          ...(isSelected && {
            backgroundColor: `${NAVY} !important`,
            color: '#ffffff !important',
            '&:hover': { backgroundColor: `${NAVY}dd !important` },
          }),
          ...(isInRange &&
            !isSelected && {
              backgroundColor: 'transparent',
              color: NAVY,
            }),
        }}
      />
    </Box>
  );
};

// ─── Calendar SX overrides ─────────────────────────────────────────────────────

const CALENDAR_SX = BASE_CALENDAR_SX;

// ─── DateRangePicker ───────────────────────────────────────────────────────────

interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disablePresets?: boolean;
  /** Maximum number of days allowed between start and end (inclusive). Defaults to MAX_RANGE_DAYS (90). */
  maxRangeDays?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  disablePresets,
  maxRangeDays = MAX_RANGE_DAYS,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState<Moment | null>(null);
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [leftMonth, setLeftMonth] = useState<Moment>(() =>
    value.start ? value.start.clone().startOf('month') : moment().startOf('month')
  );

  const triggerBlockFeedback = useCallback((msg: string) => {
    setErrorMsg(msg);
    setSnackbarOpen(true);
  }, []);

  const rightMonth = leftMonth.clone().add(1, 'month');
  const activePresetKey = matchPresetKey(value);
  const hasValue = !!(value.start || value.end);

  const handlePresetClick = useCallback(
    (preset: DateRangePreset) => {
      const built = preset.build();
      if (isRangeTooWide(built.start, built.end, maxRangeDays)) {
        triggerBlockFeedback(maxRangeMessage(maxRangeDays));
        return;
      }
      onChange(built);
      setSelecting('start');
      setHoverDate(null);
      setErrorMsg(null);
      setOpen(false);
    },
    [onChange, maxRangeDays, triggerBlockFeedback]
  );

  const handleDayClick = useCallback(
    (pickerDate: PickerValidDate | null) => {
      if (!pickerDate) return;
      const clicked = moment(pickerDate as unknown as Moment);

      if (selecting === 'start' || !value.start) {
        // Normalize to start-of-day so the boundary is inclusive from 00:00:00, mirroring the presets.
        onChange({ start: clicked.clone().startOf('day'), end: null });
        setSelecting('end');
        setErrorMsg(null);
      } else {
        const [rawStart, rawEnd] = clicked.isBefore(value.start, 'day')
          ? [clicked, value.start]
          : [value.start, clicked];
        // Snap boundaries to the full day: start at 00:00:00.000, end at 23:59:59.999 so the
        // backend's inclusive `<=` comparison includes both the opening and the closing date.
        const s = rawStart.clone().startOf('day');
        const e = rawEnd.clone().endOf('day');
        if (isRangeTooWide(s, e, maxRangeDays)) {
          // Hard block: surface snackbar + inline error, keep popover open so user can pick a closer end date.
          triggerBlockFeedback(maxRangeMessage(maxRangeDays));
          return;
        }
        onChange({ start: s, end: e });
        setSelecting('start');
        setHoverDate(null);
        setErrorMsg(null);
        setOpen(false);
      }
    },
    [selecting, value.start, onChange, maxRangeDays, triggerBlockFeedback]
  );

  const handleClear = useCallback(() => {
    onChange({ start: null, end: null });
    setSelecting('start');
    setHoverDate(null);
  }, [onChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // If only start was selected (no end), clear the partial selection
    if (value.start && !value.end) {
      onChange({ start: null, end: null });
      setSelecting('start');
    }
  }, [value, onChange]);

  // Build trigger label
  let triggerLabel: React.ReactNode;
  if (activePresetKey) {
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === activePresetKey);
    triggerLabel = preset?.label ?? 'Fecha';
  } else if (value.start && value.end) {
    triggerLabel = `${value.start.format('DD/MM/YY')} – ${value.end.format('DD/MM/YY')}`;
  } else {
    triggerLabel = (
      <Typography
        component="span"
        sx={{ color: `${NAVY}60`, fontFamily: 'Jost, sans-serif', fontSize: '0.875rem', fontWeight: 400 }}
      >
        Fecha
      </Typography>
    );
  }

  const daySlotProps = {
    rangeStart: value.start,
    rangeEnd: value.end,
    hoverDate,
    selecting,
    onDayHover: setHoverDate,
  };

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => setOpen(true)}
        endIcon={<CalendarTodayIcon sx={{ fontSize: '1rem !important', color: `${NAVY}60` }} />}
        sx={{
          height: 40,
          minWidth: 160,
          justifyContent: 'space-between',
          borderRadius: RADIUS_ITEM,
          backgroundColor: hasValue ? `${NAVY}0A` : open ? `${NAVY}06` : SURFACE_CONTAINER_LOWEST,
          color: NAVY,
          textTransform: 'none',
          fontFamily: 'Jost, sans-serif',
          fontWeight: 500,
          fontSize: '0.875rem',
          px: 2,
          whiteSpace: 'nowrap',
          border: open
            ? `1px solid rgba(21, 24, 58, 0.35)`
            : hasValue
              ? `1px solid rgba(21, 24, 58, 0.25)`
              : `1px solid rgba(21, 24, 58, 0.12)`,
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: hasValue ? `${NAVY}14` : `${NAVY}08`,
            border: `1px solid rgba(21, 24, 58, 0.35)`,
          },
        }}
      >
        {triggerLabel}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: RADIUS_CARD,
              backgroundColor: '#ffffff',
              boxShadow: '0px 16px 48px rgba(21, 24, 58, 0.14), 0px 4px 12px rgba(21, 24, 58, 0.08)',
              border: `1px solid rgba(21, 24, 58, 0.10)`,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* ── Left panel: Presets ──────────────────────── */}
          {!disablePresets && (
            <Box
              sx={{
                width: { xs: '100%', sm: 180 },
                backgroundColor: `${NAVY}05`,
                borderRight: { xs: 'none', sm: `1px solid rgba(21, 24, 58, 0.08)` },
                borderBottom: { xs: `1px solid rgba(21, 24, 58, 0.08)`, sm: 'none' },
                py: 1.5,
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                overflowX: { xs: 'auto', sm: 'visible' },
                gap: 0.25,
              }}
            >
              {DATE_RANGE_PRESETS.map((preset) => {
                const isActive = activePresetKey === preset.key;
                const built = preset.build();
                const tooWide = isRangeTooWide(built.start, built.end, maxRangeDays);
                return (
                  <Box
                    key={preset.key}
                    onClick={() => {
                      if (tooWide) {
                        triggerBlockFeedback(maxRangeMessage(maxRangeDays));
                        return;
                      }
                      handlePresetClick(preset);
                    }}
                    sx={{
                      px: 2,
                      py: 0.75,
                      cursor: 'pointer',
                      borderRadius: RADIUS_ITEM,
                      mx: { xs: 0.5, sm: 1 },
                      flexShrink: 0,
                      opacity: tooWide ? 0.5 : 1,
                      backgroundColor: isActive ? `${NAVY}0F` : 'transparent',
                      transition: 'background-color 0.15s ease-in-out',
                      '&:hover': { backgroundColor: isActive ? `${NAVY}0F` : `${NAVY}08` },
                    }}
                    title={tooWide ? maxRangeMessage(maxRangeDays) : undefined}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 700 : 400,
                        color: isActive ? NAVY : `${NAVY}99`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {preset.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Right panel: Calendars + footer ─────────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex' }} onMouseLeave={() => setHoverDate(null)}>
              {/* Left calendar */}
              <DateCalendar
                value={null}
                referenceDate={leftMonth as PickerValidDate}
                onChange={handleDayClick}
                onMonthChange={(m: PickerValidDate) => setLeftMonth(moment(m as unknown as Moment).startOf('month'))}
                slots={{ day: RangeDay as React.ElementType<PickerDayProps> }}
                slotProps={{ day: daySlotProps as Record<string, unknown> }}
                sx={CALENDAR_SX}
              />
              {/* Divider between months */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  width: '1px',
                  alignSelf: 'stretch',
                  backgroundColor: `rgba(21, 24, 58, 0.08)`,
                  flexShrink: 0,
                  my: 1.5,
                }}
              />
              {/* Right calendar — hidden on mobile */}
              <DateCalendar
                value={null}
                referenceDate={rightMonth as PickerValidDate}
                onChange={handleDayClick}
                onMonthChange={(m: PickerValidDate) =>
                  setLeftMonth(
                    moment(m as unknown as Moment)
                      .subtract(1, 'month')
                      .startOf('month')
                  )
                }
                slots={{ day: RangeDay as React.ElementType<PickerDayProps> }}
                slotProps={{ day: daySlotProps as Record<string, unknown> }}
                sx={{ ...CALENDAR_SX, display: { xs: 'none', sm: 'block' } }}
              />
            </Box>

            {/* Footer — visually separated from calendars above */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderTop: `1px solid rgba(21, 24, 58, 0.08)`,
                backgroundColor: `rgba(21, 24, 58, 0.025)`,
              }}
            >
              <Button
                onClick={handleClear}
                disabled={!value.start && !value.end}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: `${NAVY}70`,
                  px: 1,
                  minWidth: 0,
                  '&:hover': { color: NAVY, backgroundColor: 'transparent' },
                  '&.Mui-disabled': { color: `${NAVY}30` },
                }}
              >
                Limpiar selección
              </Button>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.7rem',
                  color: errorMsg ? '#c62828' : `${NAVY}70`,
                  textAlign: 'right',
                }}
              >
                {errorMsg ?? `Máximo ${maxRangeDays} días por rango`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          sx={{ fontFamily: 'Jost, sans-serif' }}
        >
          {errorMsg ?? maxRangeMessage(maxRangeDays)}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DateRangePicker;
